// Issues Management Component
// Allows moderators to view and manage reported class issues

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

interface ClassIssue {
  id: string;
  class_name: string;
  issue_type: 'incorrect_info' | 'missing_info' | 'broken_link' | 'typo' | 'other';
  title: string;
  description: string;
  reporter_id: string;
  reporter_username: string;
  reporter_avatar: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  resolution_notes?: string;
  resolver_username?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
  comment_count: number;
}

interface IssueComment {
  id: string;
  issue_id: string;
  user_id: string;
  username: string;
  avatar: string;
  is_moderator: number;
  comment: string;
  is_moderator_note: number;
  created_at: string;
}

const issueTypeLabels: Record<string, string> = {
  incorrect_info: '❌ Incorrect Info',
  missing_info: '📝 Missing Info',
  broken_link: '🔗 Broken Link',
  typo: '✏️ Typo/Grammar',
  other: '❓ Other'
};

const statusColors: Record<string, string> = {
  open: '#007bff',
  in_progress: '#ffc107',
  resolved: '#28a745',
  closed: '#6c757d'
};

const priorityColors: Record<string, string> = {
  low: '#6c757d',
  medium: '#007bff',
  high: '#fd7e14',
  critical: '#dc3545'
};

export const IssuesManagement: React.FC = () => {
  const { isAuthenticated, getAuthToken } = useAuth();
  const [issues, setIssues] = useState<ClassIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [classFilter, setClassFilter] = useState<string>('');
  const [selectedIssue, setSelectedIssue] = useState<ClassIssue | null>(null);
  const [issueDetails, setIssueDetails] = useState<{ issue: ClassIssue, comments: IssueComment[] } | null>(null);
  const [newComment, setNewComment] = useState('');
  const [isModeratorNote, setIsModeratorNote] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchIssues();
  }, [statusFilter, classFilter]);

  const fetchIssues = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (classFilter) params.append('class_name', classFilter);

      const response = await fetch(`/api/issues/list?${params}`);
      const data = await response.json();
      setIssues(data.issues || []);
    } catch (error) {
      logger.error('Failed to fetch issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchIssueDetails = async (issueId: string) => {
    try {
      const response = await fetch(`/api/issues/details?issue_id=${issueId}`);
      const data = await response.json();
      setIssueDetails(data);
    } catch (error) {
      logger.error('Failed to fetch issue details:', error);
    }
  };

  const handleSelectIssue = (issue: ClassIssue) => {
    setSelectedIssue(issue);
    fetchIssueDetails(issue.id);
  };

  const handleUpdateStatus = async (status: string) => {
    if (!selectedIssue) return;
    setUpdating(true);

    try {
      const token = getAuthToken();
      const response = await fetch('/api/issues/moderate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          issue_id: selectedIssue.id,
          action: 'update_status',
          status
        })
      });

      if (response.ok) {
        fetchIssues();
        if (issueDetails) {
          fetchIssueDetails(selectedIssue.id);
        }
      }
    } catch (error) {
      logger.error('Failed to update status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdatePriority = async (priority: string) => {
    if (!selectedIssue) return;
    setUpdating(true);

    try {
      const token = getAuthToken();
      const response = await fetch('/api/issues/moderate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          issue_id: selectedIssue.id,
          action: 'update_priority',
          priority
        })
      });

      if (response.ok) {
        fetchIssues();
        if (issueDetails) {
          fetchIssueDetails(selectedIssue.id);
        }
      }
    } catch (error) {
      logger.error('Failed to update priority:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedIssue) return;
    setUpdating(true);

    try {
      const token = getAuthToken();
      const response = await fetch('/api/issues/moderate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          issue_id: selectedIssue.id,
          action: 'resolve',
          resolution_notes: resolutionNotes
        })
      });

      if (response.ok) {
        setResolutionNotes('');
        setSelectedIssue(null);
        setIssueDetails(null);
        fetchIssues();
      }
    } catch (error) {
      logger.error('Failed to resolve issue:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAddComment = async () => {
    if (!selectedIssue || !newComment.trim()) return;
    setUpdating(true);

    try {
      const token = getAuthToken();
      const response = await fetch('/api/issues/moderate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          issue_id: selectedIssue.id,
          action: 'add_comment',
          comment: newComment,
          is_moderator_note: isModeratorNote
        })
      });

      if (response.ok) {
        setNewComment('');
        setIsModeratorNote(false);
        fetchIssueDetails(selectedIssue.id);
      }
    } catch (error) {
      logger.error('Failed to add comment:', error);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1>Issue Management</h1>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
            Status Filter:
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid var(--ifm-color-emphasis-300)'
            }}
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
            Class Name:
          </label>
          <input
            type="text"
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            placeholder="Filter by class..."
            style={{
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid var(--ifm-color-emphasis-300)'
            }}
          />
        </div>
      </div>

      {loading ? (
        <div>Loading issues...</div>
      ) : (
        <div>
          <h2>Issues ({issues.length})</h2>
            {issues.length === 0 ? (
              <p>No issues found.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {issues.map((issue) => (
                  <div
                    key={issue.id}
                    onClick={() => handleSelectIssue(issue)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid var(--ifm-color-emphasis-300)',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      backgroundColor: 'var(--ifm-background-surface-color)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--ifm-color-primary)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--ifm-color-emphasis-300)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                      {/* Left section: Title and metadata with inline badges */}
                      <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                        <h3 style={{ margin: '0 0 0.15rem 0', fontSize: '14px', fontWeight: '600' }}>
                          {issue.title}
                        </h3>
                        <div style={{ fontSize: '11px', color: 'var(--ifm-color-emphasis-600)' }}>
                          <strong>{issue.class_name}</strong> · by {issue.reporter_username}
                        </div>
                        <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.15rem', flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: '10px',
                            padding: '0.1rem 0.35rem',
                            borderRadius: '3px',
                            backgroundColor: statusColors[issue.status],
                            color: 'white',
                            fontWeight: '600'
                          }}>
                            {issue.status}
                          </span>
                          <span style={{
                            fontSize: '10px',
                            padding: '0.1rem 0.35rem',
                            borderRadius: '3px',
                            backgroundColor: priorityColors[issue.priority],
                            color: 'white',
                            fontWeight: '600'
                          }}>
                            {issue.priority}
                          </span>
                          <span style={{
                            fontSize: '10px',
                            padding: '0.1rem 0.35rem',
                            borderRadius: '3px',
                            backgroundColor: 'var(--ifm-color-emphasis-300)',
                            color: 'var(--ifm-color-emphasis-800)',
                            fontWeight: '600'
                          }}>
                            {issueTypeLabels[issue.issue_type]}
                          </span>
                        </div>
                      </div>

                      {/* Middle section: Description preview */}
                      <div style={{
                        flex: '2 1 250px',
                        fontSize: '12px',
                        color: 'var(--ifm-color-emphasis-700)',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        lineHeight: '1.3'
                      }}>
                        {issue.description}
                      </div>

                      {/* Right section: Comment count */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        marginLeft: 'auto',
                        fontSize: '11px',
                        color: 'var(--ifm-color-emphasis-600)',
                        fontWeight: '600'
                      }}>
                        <span>{issue.comment_count} 💬</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {/* Issue Details Modal */}
      {selectedIssue && issueDetails && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setSelectedIssue(null)}
        >
          <div
            style={{
              backgroundColor: 'var(--ifm-background-color)',
              borderRadius: '12px',
              padding: '2rem',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0 }}>{issueDetails.issue.title}</h2>
              <button
                onClick={() => setSelectedIssue(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--ifm-color-emphasis-600)',
                  padding: '0.25rem'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                  <strong>Class:</strong> {issueDetails.issue.class_name}
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Type:</strong> {issueTypeLabels[issueDetails.issue.issue_type]}
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Description:</strong>
                  <p style={{ marginTop: '0.5rem' }}>{issueDetails.issue.description}</p>
                </div>
              </div>

              {/* Moderator Actions */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--ifm-color-emphasis-100)', borderRadius: '8px' }}>
                <h3>Moderator Actions</h3>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Status:
                  </label>
                  <select
                    value={issueDetails.issue.status}
                    onChange={(e) => handleUpdateStatus(e.target.value)}
                    disabled={updating}
                    style={{
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: '1px solid var(--ifm-color-emphasis-300)'
                    }}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Priority:
                  </label>
                  <select
                    value={issueDetails.issue.priority}
                    onChange={(e) => handleUpdatePriority(e.target.value)}
                    disabled={updating}
                    style={{
                      padding: '0.5rem',
                      borderRadius: '4px',
                      border: '1px solid var(--ifm-color-emphasis-300)'
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                {issueDetails.issue.status !== 'resolved' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                      Resolve Issue:
                    </label>
                    <textarea
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                      placeholder="Resolution notes (optional)..."
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid var(--ifm-color-emphasis-300)',
                        marginBottom: '0.5rem'
                      }}
                    />
                    <button
                      onClick={handleResolve}
                      disabled={updating}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: updating ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Mark as Resolved
                    </button>
                  </div>
                )}
              </div>

              {/* Comments */}
              <div>
                <h3>Comments ({issueDetails.comments.length})</h3>
                <div style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '1rem' }}>
                  {issueDetails.comments.map((comment) => (
                    <div
                      key={comment.id}
                      style={{
                        padding: '1rem',
                        marginBottom: '1rem',
                        backgroundColor: comment.is_moderator_note ? 'rgba(255, 193, 7, 0.1)' : 'var(--ifm-background-color)',
                        border: '1px solid var(--ifm-color-emphasis-300)',
                        borderRadius: '8px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <img
                          src={comment.avatar}
                          alt={comment.username}
                          style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                        />
                        <strong>{comment.username}</strong>
                        {comment.is_moderator && <span style={{ fontSize: '0.75rem', color: '#007bff' }}>👑 Moderator</span>}
                        {comment.is_moderator_note === 1 && <span style={{ fontSize: '0.75rem', color: '#ffc107' }}>🔒 Private Note</span>}
                      </div>
                      <p style={{ margin: '0.5rem 0' }}>{comment.comment}</p>
                      <div style={{ fontSize: '0.75rem', color: 'var(--ifm-color-emphasis-600)' }}>
                        {formatDate(comment.created_at)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Comment */}
                <div>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '4px',
                      border: '1px solid var(--ifm-color-emphasis-300)',
                      marginBottom: '0.5rem'
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
                      <input
                        type="checkbox"
                        checked={isModeratorNote}
                        onChange={(e) => setIsModeratorNote(e.target.checked)}
                      />
                      Private moderator note
                    </label>
                    <button
                      onClick={handleAddComment}
                      disabled={updating || !newComment.trim()}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: 'var(--ifm-color-primary)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: (updating || !newComment.trim()) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Add Comment
                    </button>
                  </div>
                </div>
              </div>
          </div>
        </div>
      )}
    </div>
  );
};
